<template>
  <section v-if="slider_items.length" class="hero hero--slider">
    <swiper-container
      init="false"
      ref="swiper-container"
      class="hero--slider-main"
      pagination="true"
    >
      <swiper-slide
        v-for="(item, item_index) in slider_items"
        :key="_uid + item_index"
        class="hero--slider-item"
        :class="{ overlay: item.overlay }"
      >
        <div
          class="hero--slider-image"
          :style="'background-image:url(' + item.wallpaper + ')'"
        ></div>
        <b-container class="captions">
          <vb-icon
            :name="'symbol_lb'"
            :size="36"
            class="icon"
            :color="'#999999'"
          />
          <div class="text">
            <div class="vb-heading h2" v-html="item.title" />
            <div class="text-md" v-html="item.text" />
          </div>
        </b-container>
      </swiper-slide>
    </swiper-container>
    <template v-if="has_multiple_slides">
      <vb-icon
        :name="'angle_left'"
        :color="'000'"
        :size="18"
        class="hero--slider-nav hero--slider-nav--prev"
        @click="swiper_instance.swiper.slidePrev()"
      />
      <vb-icon
        :name="'angle_right'"
        :color="'000'"
        :size="18"
        class="hero--slider-nav hero--slider-nav--next"
        @click="swiper_instance.swiper.slideNext()"
      />
      <div class="custom-pagination">
        <div
          class="custom-pagination-item"
          v-for="(item, item_index) in Array.from(
            { length: slider_items.length / 2 },
            (_, i) => i,
          )"
          :key="_uid + item_index"
          :class="{ active: slider_index === item_index }"
        />
      </div>
    </template>
  </section>
</template>

<script>
import { get_config, is_authenticated } from "@src/utilities";
import { md } from "@running_app_src/scss/abstract/_custom.scss";

export default {
  name: "hero",
  props: {
    // Sub-key of `sliderHome` holding a dedicated slider for the selected
    // product (es. 'alpitour', 'sololand'). Takes precedence over the
    // public/private split when configured and non-empty; empty means keep it.
    sliderVariant: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      slider_items: this.get_slider_items(),
      slider_index: 0,
      swiper_instance: {},
      swiper_params: {
        slidesPerView: 1,
        loop: true,
        autoHeight: true,
        breakpoints: {
          [md.replace(/[^\d.]+/g, "")]: {
            slidesPerView: 2,
          },
        },
        on: {
          init: (s) => {
            // forzo l'aggiornamento senza cambiare slide per aggiornare la autoHeight
            // s.slideTo(0)
            // aggiorno slider_index per avere l'active nella paginazione
            this.slider_index = s.realIndex;
            // aggiorno l'istanza in base al numero di slides:
            // se loopedSlides==1 sono nel mobile (default: chiamo la update() ma di fatto non succede niente)
            // se loopedSlides==2 sono nel desktop e devo manipolare l'ordine delle slides per mostrare correttamente l'ultima in trasparenza
            this.slider_items = this.get_slider_items(s.loopedSlides);
            s.update();
          },
          /*
          slideChange: event => {
            // aggiorno slider_index per avere l'active nella paginazione
            this.slider_index = event.realIndex
          },
          */
          slidePrevTransitionStart: () => {
            if (this.slider_index == 0)
              this.slider_index = this.slider_items.length / 2 - 1;
            else this.slider_index = this.slider_index - 1;
          },
          slideNextTransitionStart: () => {
            if (this.slider_index == this.slider_items.length / 2 - 1)
              this.slider_index = 0;
            else this.slider_index = this.slider_index + 1;
          },
        },
      },
    };
  },
  computed: {
    has_multiple_slides() {
      return this.get_slider_items().length > 1;
    },
  },

  mounted() {
    const swiper_instance = this.$refs["swiper-container"];
    Object.assign(swiper_instance, this.swiper_params);
    swiper_instance.initialize();
    this.swiper_instance = swiper_instance;
  },

  methods: {
    get_configured_slider_items() {
      // A provider slider is a flat array with no public/private split, so once
      // configured it serves anonymous and authenticated visitors alike
      if (this.sliderVariant) {
        const variant_items = get_config(
          `sliderHome.${this.sliderVariant}`,
          [],
        );
        if (variant_items.length) {
          return variant_items;
        }
      }
      if (!is_authenticated()) {
        return get_config("sliderHome.public", []);
      }
      return get_config("sliderHome.private", []);
    },
    get_slider_items(slides = 1) {
      const items = this.get_configured_slider_items();
      if (!items.length || slides === 1) {
        return items;
      }
      const cloned = [...items];
      const last = cloned.pop();
      return [last, ...cloned, last, ...cloned];
    },
  },
};
</script>

<style lang="scss" scoped>
.hero {
  &--slider {
    position: relative;
    margin-top: 0;

    @include media-breakpoint-up(md) {
      margin-bottom: 0;
    }

    &-item {
      position: relative;
    }

    &-image {
      background-size: cover;
      background-position: center center;
      height: 280px;

      @include media-breakpoint-up(md) {
        height: 320px;
      }
      @include media-breakpoint-up(lg) {
        height: 360px;
      }
    }

    &-nav {
      position: absolute;
      z-index: 1;
      top: 130px;
      transform: translateY(-50%);
      padding: $grid-gutter-width/2;
      cursor: pointer;

      &--prev {
        left: 0;
        ::v-deep {
          svg {
            filter: drop-shadow(-2px 0 0 $white);
          }
        }
      }

      &--next {
        right: 0;
        ::v-deep {
          svg {
            filter: drop-shadow(2px 0 0 $white);
          }
        }
      }

      @include media-breakpoint-up(md) {
        top: 50%;
      }
    }
    .captions {
      margin-top: $grid-gutter-width/2;
      display: flex;
      .icon {
        padding-top: 3px;
      }

      .text {
        padding-left: $grid-gutter-width/2;
        color: $dark;
        * {
          line-height: 120%;
        }
        .vb-heading {
          margin-bottom: 4px;
          color: $dark;
          line-height: 110%;
        }
      }

      @include media-breakpoint-between(md, lg) {
        padding-left: 16% !important;

        .vb-heading {
          font-size: 30px !important;
          max-width: 90% !important;
        }
      }
    }

    .custom-pagination {
      display: none;
      position: absolute;
      z-index: 1;
      bottom: 100px;
      left: calc(
        2.5% + 36px + #{$grid-gutter-width/2}
      ); // margine sinistro + larghezza icona + padding contenitore captions

      &-item {
        margin-right: 10px;
        height: 2px;
        width: 30px;
        background: $gray-500;
        &.active {
          background: $gray-600;
        }
      }
    }

    @include media-breakpoint-up(md) {
      &-item {
        &:after {
          content: "";
          position: absolute;
          background: rgba($white, 0.9);
          top: 0;
          left: 0;
          right: 0;
          width: 100%;
          height: 100%;
          transition: $transition-base;
          opacity: 0;
        }
      }

      .custom-pagination {
        display: flex;
        margin-left: 10.5%;
      }

      .swiper-slide-active {
        .captions {
          display: none;
        }

        &:after {
          opacity: 1;
        }
      }

      .swiper-slide-next {
        position: relative;

        .captions {
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          transform: translateY(-50%) translateX(-100%);
          margin-top: 0;
          padding: 0 $grid-gutter-width 0 26%;

          .text-md {
            font-size: 18px;
          }

          .vb-heading {
            font-size: 36px;
            line-height: 120%;
          }

          .icon {
            padding-top: 8px;
          }
        }
      }
    }
  }

  &--booking-engine {
    position: relative;
    z-index: 1;

    @include media-breakpoint-up(md) {
      margin-top: -33px;
    }
  }
}
</style>
